import React from "react";
import { fmtEUR } from "../../lib/format";
import { TrendingUp } from "lucide-react";

/**
 * Bandeau d'impact — sobre, pas de bloc coloré plein.
 * Filet fin + petit point d'accent selon l'état.
 */
export default function ImpactBanner({ impact, size = "large" }) {
  if (!impact) return null;
  const isCompact = size === "compact";

  if (!impact.sufficient) {
    return (
      <div
        data-testid="impact-banner"
        className={isCompact ? "px-3 py-2 text-xs" : "px-5 py-4 mb-8"}
        style={{
          backgroundColor: "var(--th-surface)",
          border: "1px solid var(--th-border)",
          color: "var(--th-text-3)",
          borderRadius: isCompact ? 8 : 12,
        }}
      >
        <span className="inline-flex items-center gap-2">
          <TrendingUp size={isCompact ? 12 : 14} />
          <span>
            {isCompact
              ? "Impact : données insuffisantes"
              : "Données insuffisantes pour mesurer l'impact — revenez après quelques relances."}
          </span>
        </span>
      </div>
    );
  }

  const days = impact.window_days || 30;
  const period = days === 30 ? "Ce mois-ci" : `Sur ${days} j`;

  if (isCompact) {
    return (
      <div
        data-testid="impact-banner"
        className="px-3 py-2 text-xs inline-flex items-center gap-2"
        style={{
          backgroundColor: "var(--th-surface)",
          border: "1px solid var(--th-border)",
          color: "var(--th-text-2)",
          borderRadius: 8,
        }}
      >
        <span
          style={{
            width: 6, height: 6, borderRadius: "50%",
            backgroundColor: "var(--th-faible-text)",
          }}
        />
        <span>
          Tes relances du mois :{" "}
          <span
            style={{
              fontVariantNumeric: "tabular-nums",
              fontWeight: 500,
              color: "var(--th-text)",
            }}
          >
            {fmtEUR(impact.montant_signe)}
          </span>{" "}
          signés ({impact.nb_signees_apres_relance}/{impact.nb_relancees})
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="impact-banner"
      className="p-5 mb-8 flex flex-wrap items-center gap-x-10 gap-y-3"
      style={{
        backgroundColor: "var(--th-surface)",
        border: "1px solid var(--th-border)",
        color: "var(--th-text-2)",
        borderRadius: 12,
      }}
    >
      <div className="flex items-center gap-2.5" style={{ fontWeight: 500, color: "var(--th-text)" }}>
        <span
          style={{
            width: 8, height: 8, borderRadius: "50%",
            backgroundColor: "var(--th-faible-text)",
          }}
        />
        Impact du mois — {period}
      </div>
      <div className="flex gap-8 text-sm flex-wrap">
        <div>
          <span
            style={{
              fontFamily: "var(--th-font-sans)",
              fontSize: 20,
              fontWeight: 500,
              fontVariantNumeric: "tabular-nums",
              color: "var(--th-text)",
            }}
          >
            {impact.nb_relancees}
          </span>
          <span className="ml-2" style={{ color: "var(--th-text-3)" }}>propales relancées</span>
        </div>
        <div>
          <span
            style={{
              fontFamily: "var(--th-font-sans)",
              fontSize: 20,
              fontWeight: 500,
              fontVariantNumeric: "tabular-nums",
              color: "var(--th-text)",
            }}
          >
            {impact.nb_signees_apres_relance}
          </span>
          <span className="ml-2" style={{ color: "var(--th-text-3)" }}>signées ensuite</span>
        </div>
        <div>
          <span
            style={{
              fontFamily: "var(--th-font-sans)",
              fontSize: 20,
              fontWeight: 500,
              fontVariantNumeric: "tabular-nums",
              color: "var(--th-text)",
            }}
          >
            {fmtEUR(impact.montant_signe)}
          </span>
          <span className="ml-2" style={{ color: "var(--th-text-3)" }}>récupérés</span>
        </div>
        {impact.nb_clients_touches > 0 && (
          <div>
            <span
              style={{
                fontFamily: "var(--th-font-sans)",
                fontSize: 20,
                fontWeight: 500,
                fontVariantNumeric: "tabular-nums",
                color: "var(--th-text)",
              }}
            >
              {impact.nb_clients_touches}
            </span>
            <span className="ml-2" style={{ color: "var(--th-text-3)" }}>
              client{impact.nb_clients_touches > 1 ? "s" : ""} touché
              {impact.nb_clients_touches > 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
